import React, { useState } from 'react'
import { Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel } from '@mui/material'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { formatBigMoney, formatDateYYYYMMDD, formatMoney } from '../../../packages/utils/numberFormatters'
import { useTranslation } from 'react-i18next'

const descendingComparator = (a, b) => {
    if (b.date === null || a.date === null) return 0

    if (b.date < a.date) {
        return -1
    }
    if (b.date > a.date) {
        return 1
    }
    return 0
}

const getComparator = (order: 'desc' | 'asc') => {
    return order === 'desc' ? (a, b) => descendingComparator(a, b) : (a, b) => -descendingComparator(a, b)
}

function parseOptionalLoanAmount(value: string | number | null): number | null {
    if (value === null || value === '') return null
    const n = typeof value === 'number' ? value : parseFloat(value)
    return Number.isFinite(n) ? n : null
}

function getDisplayedActionRows(convertible: ConvertibleEntity) {
    const sortedConvertibleActions = [...convertible.convertibleActions].sort((a, b) => {
        const da = new Date(a.recordDate).getTime()
        const db = new Date(b.recordDate).getTime()
        return da - db
    })

    return sortedConvertibleActions.map((action) => {
        let amount: number | null = null
        if (action.actionType === 'new_loan' && action.loanAmount !== null) {
            amount = parseOptionalLoanAmount(action.loanAmount)
        } else if (action.repayment !== null) {
            amount = action.repayment
        } else if (action.convertedShares !== null && action.conversionPrice !== null) {
            amount = action.convertedShares * action.conversionPrice
        }
        return {
            id: action.id,
            date: action.recordDate,
            type: action.actionType,
            amount,
            conversionPrice: action.conversionPrice,
        }
    })
}

function ConvertibleEvents({ convertible }: { convertible: ConvertibleEntity }) {
    const [order, setOrder] = useState<'desc' | 'asc'>('asc')

    const { i18n, t } = useTranslation()
    const locale = i18n.language

    const sortOrder = () => {
        setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    }

    const tableRows = getDisplayedActionRows(convertible)

    const emptyState = tableRows.length === 0

    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell sortDirection={order}>
                        <TableSortLabel active direction={order} onClick={() => sortOrder()}>
                            Datum
                        </TableSortLabel>
                    </TableCell>
                    <TableCell>Aktivitet</TableCell>
                    <TableCell>Belopp</TableCell>
                    <TableCell>Konverteringskurs</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {emptyState ? (
                    <TableRow>
                        <TableCell colSpan={5} align='center'>
                            Inga händelser för konvertibeln är registrerade
                        </TableCell>
                    </TableRow>
                ) : null}
                {tableRows.sort(getComparator(order)).map((row) => {
                    return (
                        <TableRow key={row.id}>
                            <TableCell>{formatDateYYYYMMDD(row.date)}</TableCell>
                            <TableCell>{t(`entities.convertible.fields.${row.type}`)}</TableCell>
                            <TableCell>
                                {row.amount !== null
                                    ? formatBigMoney(row.amount, { locale, currencySymbol: convertible.currency })
                                    : '-'}
                            </TableCell>
                            <TableCell>
                                {row.conversionPrice
                                    ? formatMoney(row.conversionPrice, {
                                          minDecimals: 2,
                                          maxDecimals: 5,
                                          locale,
                                      })
                                    : '-'}
                            </TableCell>
                        </TableRow>
                    )
                })}
            </TableBody>
        </Table>
    )
}
export default ConvertibleEvents
